import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import { Logo } from "./Logo";
import { WhatsAppIcon } from "./WhatsAppIcon";
import { NAV, WA_GENERAL, scrollToId, slug } from "../lib/site";

export const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (id) => { setOpen(false); scrollToId(id); };

  return (
    <motion.header
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1], delay: 0.2 }}
      className="fixed top-0 inset-x-0 z-50 px-3 sm:px-5 pt-3"
      data-testid="site-header"
    >
      <div className={`mx-auto max-w-7xl flex items-center justify-between rounded-full pl-4 pr-2 py-2 transition-[background-color,box-shadow,backdrop-filter] duration-500 ${scrolled ? "bg-white/80 backdrop-blur-xl shadow-[0_10px_40px_-15px_rgba(0,0,0,0.25)]" : "bg-white/60 backdrop-blur-md"}`}>
        <button onClick={() => go("home")} data-testid="header-logo-btn" aria-label="Go Trip Travels home">
          <Logo />
        </button>
        <nav className="hidden lg:flex items-center gap-1">
          {NAV.map((n) => (
            <button key={n.id} onClick={() => go(n.id)} data-testid={`nav-${slug(n.label)}`}
              className="group relative px-4 py-2 text-sm font-semibold text-ink">
              <span className="relative z-10">{n.label}</span>
              <span className="absolute inset-x-4 bottom-1.5 h-[2px] origin-left scale-x-0 bg-go transition-transform duration-300 group-hover:scale-x-100" />
            </button>
          ))}
        </nav>
        <div className="flex items-center gap-2">
          <a href={WA_GENERAL} target="_blank" rel="noopener noreferrer" data-testid="header-whatsapp-btn"
            className="group flex items-center gap-2 rounded-full bg-go px-4 sm:px-5 py-2.5 text-sm font-bold text-ink transition-[background-color,transform] duration-300 hover:bg-ink hover:text-white active:scale-95">
            <WhatsAppIcon className="w-4 h-4" />
            <span className="hidden sm:inline">Book via WhatsApp</span>
            <span className="sm:hidden">Book</span>
          </a>
          <button onClick={() => setOpen((o) => !o)} className="lg:hidden grid place-items-center w-10 h-10 rounded-full bg-ink text-white" data-testid="mobile-menu-toggle" aria-label="Menu">
            {open ? <X size={18} /> : <Menu size={18} />}
          </button>
        </div>
      </div>
      <AnimatePresence>
        {open && (
          <motion.nav initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }}
            className="lg:hidden mx-auto max-w-7xl mt-2 rounded-3xl bg-ink p-4 text-white" data-testid="mobile-menu">
            {NAV.map((n, i) => (
              <button key={n.id} onClick={() => go(n.id)} data-testid={`mobile-nav-${slug(n.label)}`}
                className="flex w-full items-baseline gap-3 border-b border-white/10 py-3 text-left font-display text-2xl font-bold last:border-0">
                <span className="font-mono text-xs text-go">0{i + 1}</span>{n.label}
              </button>
            ))}
          </motion.nav>
        )}
      </AnimatePresence>
    </motion.header>
  );
};
